<template>
<div class="image-uploader">
    <div class="upload-box" @dragover.prevent @drop.prevent="handleDrop" :class="{ 'drag-over': isDragging }" @dragenter.prevent="isDragging = true" @dragleave.prevent="isDragging = false">
        <input type="file" ref="fileInput" @change="handleFileChange" accept="image/*" class="file-input">
        <div class="upload-content">
            <div v-if="loading" class="loading">Processing...</div>
            <div v-else-if="base64Image" class="preview">
                <img :src="'data:image/jpeg;base64,' + base64Image" alt="Preview">
                <p class="upload-info">Click or drag to upload a new image</p>
            </div>
            <div v-else class="upload-prompt">
                <i class="upload-icon"><img src="@/assets/images/upload-icon.svg"></i>
                <p>Drop image here or click to upload</p>
            </div>
        </div>
    </div>
    <div v-if="error" class="error-message">
        {{ error }}
        <div class="error-action">Please try uploading again</div>
    </div>
    <button class="form-button w-button balugabtn" :disabled="!base64Image || loading || uploading" :class="{ 'error-state': error }" @click="confirmImage">
        {{ buttonText }}
    </button>
</div>
</template>

<script>
export default {
    name: 'ImageUploader',

    props: {
        imageType: {
            type: String,
            required: true,
            validator: (value) => {
                return ['full_body_image', 'id_image', 'prescription_image',
                    'font_facing_image', 'top_head_image', 'id_front_image',
                    'id_back_image'
                ].includes(value)
            }
        },
        idOfForm: {
            type: Number,
            required: true
        },
        questionId: {
            type: Number,
            required: true
        }
    },

    data() {
        return {
            base64Image: '',
            error: '',
            loading: false,
            uploading: false,
            uploadComplete: false,
            isDragging: false,
            baseUrl: process.env.VUE_APP_API_ENDPOINT,
        }
    },

    computed: {
        buttonText() {
            if (this.uploading) return 'Uploading...'
            if (this.error) return 'Try Again'
            if (this.uploadComplete) return 'Uploaded!'
            return 'Confirm Upload'
        }
    },

    watch: {
        questionId() {
            this.base64Image = ''
            this.uploadComplete = false
            this.error = ''
        }
    },

    methods: {
        async compressImage(file) {
            return new Promise((resolve) => {
                const reader = new FileReader()
                reader.readAsDataURL(file)

                reader.onload = (event) => {
                    const img = new Image()
                    img.src = event.target.result

                    img.onload = () => {
                        const canvas = document.createElement('canvas')
                        let width = img.width
                        let height = img.height

                        // Scale down to max width of 1000px while maintaining aspect ratio
                        if (width > 1000) {
                            height = Math.round((height * 1000) / width)
                            width = 1000
                        }

                        canvas.width = width
                        canvas.height = height

                        const ctx = canvas.getContext('2d')
                        ctx.drawImage(img, 0, 0, width, height)

                        // Convert to JPEG and compress
                        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8)
                        // Remove MIME type prefix
                        const base64String = compressedBase64.split(',')[1]
                        resolve(base64String)
                    }
                }
            })
        },

        async processFile(file) {
            this.error = ''
            this.loading = true
            this.base64Image = ''
            this.uploadComplete = false
            this.uploading = false // Reset uploading state when processing new file

            try {
                if (!file) {
                    throw new Error('Please select a file')
                }

                if (!file.type.startsWith('image/')) {
                    throw new Error('Please upload an image file')
                }

                const base64String = await this.compressImage(file)
                this.base64Image = base64String
            } catch (err) {
                this.error = err.message
            } finally {
                this.loading = false
            }
        },

        handleFileChange(event) {
            const file = event.target.files[0]
            if (file) {
                this.processFile(file)
            }
        },

        handleDrop(event) {
            this.isDragging = false
            const file = event.dataTransfer.files[0]
            if (file) {
                this.processFile(file)
            }
        },

        async getSignedUrl(filename, contentType) {
            try {
                const response = await fetch(`${this.baseUrl}/api/get-signed-url`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        filename,
                        content_type: contentType
                    })
                })

                if (!response.ok) {
                    throw new Error('Failed to get signed URL')
                }

                return await response.json()
            } catch (error) {
                throw new Error('Error getting signed URL: ' + error.message)
            }
        },

        async uploadToS3(signedUrl, base64Data) {
            try {
                // Create blob for text file containing base64 data
                const textBlob = new Blob([base64Data], {
                    type: 'text/plain'
                })

                // Upload text file
                const response = await fetch(signedUrl, {
                    method: 'PUT',
                    body: textBlob,
                    headers: {
                        'Content-Type': 'text/plain',
                    },
                })

                if (!response.ok) {
                    throw new Error('Failed to upload file to S3')
                }
            } catch (error) {
                throw new Error('Error uploading to S3: ' + error.message)
            }
        },

        async confirmImage() {
            if (!this.base64Image || this.uploading) return

            this.uploading = true
            this.error = ''
            this.uploadComplete = false

            try {
                // Generate unique filename for text file
                const timestamp = new Date().getTime()
                const filename = `${this.idOfForm}_${this.imageType}_${timestamp}.txt`

                // Get signed URL for text file
                const urlData = await this.getSignedUrl(filename, 'text/plain')
                if (!urlData?.url) {
                    throw new Error('Failed to get upload URL')
                }

                // Upload file to S3
                await this.uploadToS3(urlData.url, this.base64Image)

                // Set upload complete
                this.uploadComplete = true

                // Only emit on success
                this.$emit('image-uploaded', {
                    type: this.imageType,
                    base64: filename
                })

            } catch (err) {
                // Handle error locally
                if (err.message.includes('Failed to fetch') || err.message.includes('Network')) {
                    this.error = 'Network error. Please check your connection and try again.'
                } else if (err.message.includes('signed URL')) {
                    this.error = 'Error preparing upload. PPlease try using a smaller image size.'
                } else if (err.message.includes('S3')) {
                    this.error = 'Error uploading file. Please try using a smaller image size.'
                } else {
                    this.error = 'The upload was unsuccessful. Please try using a smaller image size.'
                }
                // Reset states
                this.uploadComplete = false
            } finally {
                this.uploading = false
            }
        }
    }
}
</script>

<style scoped>
.balugabtn {
    padding-top: 15px !important;
}

.image-uploader {
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
}

.upload-box {
    position: relative;
    border: 2px dashed #ccc;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-bottom: 15px;
}

.upload-box:hover {
    border-color: #666;
}

.drag-over {
    border-color: #2196F3;
    background-color: rgba(33, 150, 243, 0.1);
}

.file-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
}

.upload-content {
    padding: 20px;
}

.upload-icon {
    font-size: 48px;
    margin-bottom: 10px;
}

.upload-prompt p {
    margin: 10px 0;
    font-size: 16px;
    color: #666;
}

.upload-info {
    font-size: 14px;
    color: #999;
}

.preview {
    margin: 10px 0;
}

.preview img {
    max-width: 100%;
    max-height: 300px;
    border-radius: 4px;
}

.loading {
    padding: 20px;
    color: #666;
}

.error-message {
    color: #f44336;
    margin-top: 10px;
    font-size: 14px;
    padding: 10px;
    background-color: #ffebee;
    border-radius: 4px;
    border: 1px solid #ffcdd2;
}

.error-action {
    margin-top: 5px;
    font-weight: 500;
}

.error-state {
    background-color: #f44336 !important;
    border-color: #d32f2f !important;
}

.error-state:hover {
    background-color: #d32f2f !important;
}
</style>
